import type { JSONContent } from "@tiptap/core";
import { getDisplayLabelFromAssetRef, parseBucketQualifiedAssetRef } from "@/features/image-generation";
import {
  chapterStatusLabels,
  cloneEmptyStoryDocument,
  sideStoryCanonStatuses,
  sideStoryCanonStatusLabels,
  storyModeLabels,
  type ChapterRecord,
  type ChapterView,
  type MainStoryView,
  type SideStoryView,
  type StoryAttachmentView,
  type StoryCapacitySummary,
  type StoryLimitPrompt,
  type StoryRecord,
} from "@/types/story";
import type { SubscriptionTier } from "@/types/projects";

export function normalizeStoryDocument(input: unknown): JSONContent {
  if (typeof input === "object" && input !== null && "type" in (input as Record<string, unknown>)) {
    return input as JSONContent;
  }

  return cloneEmptyStoryDocument();
}

export function mapStoryAttachment(
  path: string,
  imageUrl: string | null,
  generatedFallbackLabel?: string | null,
): StoryAttachmentView {
  const segments = path.split("/").filter(Boolean);
  const rawFileName = segments.at(-1) ?? path;
  const parsedAssetRef = parseBucketQualifiedAssetRef(path);
  const fileName =
    parsedAssetRef?.bucket === "generated-images" && generatedFallbackLabel
      ? `${generatedFallbackLabel} illustration`
      : getDisplayLabelFromAssetRef(path) || rawFileName;

  return {
    id: path,
    fileName,
    imageUrl,
    path,
  };
}

export function mapChapterRecordToView(
  record: ChapterRecord,
  imageUrl: string | null,
): ChapterView {
  return {
    id: record.id,
    storyId: record.story_id,
    title: record.title,
    body: normalizeStoryDocument(record.body),
    status: record.status,
    statusLabel: chapterStatusLabels[record.status],
    sortOrder: record.sort_order,
    isOpening: record.is_opening,
    isClosing: record.is_closing,
    imagePath: record.image_path,
    imageUrl,
    createdAt: record.created_at,
    updatedAt: record.updated_at,
    hasHeaderImage: Boolean(record.image_path),
    headerImageUrl: imageUrl,
    headerTargetLabel: "Chapter image",
    headerGenerateActionLabel: "story.attachments.generateIllustration",
  };
}

export function mapMainStoryRecordToView(
  record: StoryRecord,
  chapters: ChapterView[],
): MainStoryView {
  return {
    id: record.id,
    projectId: record.project_id,
    mode: record.mode,
    modeLabel: storyModeLabels[record.mode],
    title: record.title,
    summary: record.summary ?? "",
    body: normalizeStoryDocument(record.body),
    chapters: [...chapters].sort((left, right) => left.sortOrder - right.sortOrder),
    createdAt: record.created_at,
    updatedAt: record.updated_at,
  };
}

export function mapSideStoryRecordToView(
  record: StoryRecord,
  imageUrls: Array<string | null>,
): SideStoryView {
  const paths = Array.isArray(record.image_paths) ? record.image_paths : [];
  const canonStatus = sideStoryCanonStatuses.includes(record.canon_status)
    ? record.canon_status
    : "draft";

  return {
    id: record.id,
    projectId: record.project_id,
    canonStatus,
    canonStatusLabelKey: sideStoryCanonStatusLabels[canonStatus],
    mode: "continuous",
    modeLabel: storyModeLabels.continuous,
    title: record.title,
    summary: record.summary ?? "",
    body: normalizeStoryDocument(record.body),
    attachments: paths.map((path, index) =>
      mapStoryAttachment(path, imageUrls[index] ?? null, record.title),
    ),
    createdAt: record.created_at,
    updatedAt: record.updated_at,
  };
}

export function mapStoryCapacityRow(input: {
  chapter_count: number;
  chapter_limit: number | null;
  chapters_remaining: number | null;
  side_story_count: number;
  side_story_limit: number | null;
  side_stories_remaining: number | null;
  tier: SubscriptionTier;
}): StoryCapacitySummary {
  return {
    tier: input.tier,
    chapterCount: input.chapter_count,
    chapterLimit: input.chapter_limit,
    chaptersRemaining: input.chapters_remaining,
    sideStoryCount: input.side_story_count,
    sideStoryLimit: input.side_story_limit,
    sideStoriesRemaining: input.side_stories_remaining,
    isChapterUnlimited: input.chapter_limit === null,
    isSideStoryUnlimited: input.side_story_limit === null,
  };
}

export function parseStoryLimitError(message: string): StoryLimitPrompt | null {
  const [prefix, tier, currentCount, allowedCount, recommendedTier] = message.split("|");

  if (
    (prefix !== "CHAPTER_LIMIT_REACHED" && prefix !== "SIDE_STORY_LIMIT_REACHED")
    || !tier
    || !currentCount
    || !allowedCount
    || !recommendedTier
  ) {
    return null;
  }

  return {
    reason: prefix === "CHAPTER_LIMIT_REACHED" ? "chapter_limit_reached" : "side_story_limit_reached",
    tier: tier as SubscriptionTier,
    currentCount: Number(currentCount),
    allowedCount: Number(allowedCount),
    recommendedTier: recommendedTier as SubscriptionTier,
  };
}

export function buildProjectSourceHref(
  projectId: string,
  sourceKind: "chapter" | "lore_entry",
  sourceRecordId: string | null | undefined,
) {
  if (!sourceRecordId) {
    return null;
  }

  if (sourceKind === "chapter") {
    return `/projects/${projectId}/stories?chapterId=${sourceRecordId}`;
  }

  return `/projects/${projectId}/lore/entries/${sourceRecordId}`;
}
